import React, { useState, useEffect } from "react";
import pana from "../assets/pana.png";
import { Link, useParams } from "react-router-dom";
import UserService from "../services/userService";
import useTheme from "../hooks/useTheme";

function Welcome() {
  const { slug } = useParams();
  const { applyTheme } = useTheme();

  const [isLoading, setIsLoading] = useState(!!slug);
  const [welcomeHeader, setWelcomeHeader] = useState("Welcome to Misas");
  const [welcomeWriteup, setWelcomeWriteup] = useState(
    "We are committed to providing exceptional healthcare services and personalized care to our patients. Your health is our top priority, and we strive to make your experience with us comfortable and healing. We look forward to serving you and being a part of your journey to better health."
  );
  const [hospitalName, setHospitalName] = useState("Misas");

  useEffect(() => {
    if (slug) {
      setIsLoading(true);

      const fetchPromise = UserService.getHospitalBySlug(slug);

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Timeout")), 10000);
      });

      Promise.race([fetchPromise, timeoutPromise])
        .then((response) => {
          if (response?.success && response?.data) {
            const hospital = response.data;
            setHospitalName(hospital.name || "Misas");
            setWelcomeHeader(hospital.welcome_header || `Welcome to ${hospital.name}`);
            setWelcomeWriteup(
              hospital.welcome_writeup ||
              "We are committed to providing exceptional healthcare services and personalized care to our patients. Your health is our top priority, and we strive to make your experience with us comfortable and healing. We look forward to serving you and being a part of your journey to better health."
            );

            // Save to localStorage
            localStorage.setItem("hospital_primary_color", JSON.stringify(hospital.primary_color));
            localStorage.setItem("hospital_secondary_color", JSON.stringify(hospital.secondary_color));
            localStorage.setItem("hospital_opaque_color", JSON.stringify(hospital.opaque_color));
            localStorage.setItem("hospital_name", JSON.stringify(hospital.name));
            localStorage.setItem("hospital_welcome_header", JSON.stringify(hospital.welcome_header));
            localStorage.setItem("hospital_welcome_writeup", JSON.stringify(hospital.welcome_writeup));
            localStorage.setItem("hospital_id", JSON.stringify(hospital.hospital_id));
            localStorage.setItem("hospital", JSON.stringify(hospital.hospital_id));

            // Apply theme instantly
            applyTheme({
              primary_color: hospital.primary_color,
              secondary_color: hospital.secondary_color,
              opaque_color: hospital.opaque_color,
            });
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error loading hospital by slug:", err);

          // Clear any stored hospital branding from localStorage to use defaults
          const keysToRemove = [
            "hospital_primary_color",
            "hospital_secondary_color",
            "hospital_opaque_color",
            "hospital_name",
            "hospital_welcome_header",
            "hospital_welcome_writeup",
            "hospital_id",
            "hospital",
          ];
          keysToRemove.forEach((key) => localStorage.removeItem(key));
          applyTheme(null);
          setIsLoading(false);
        });
    } else {
      // Clear localStorage hospital branding if landing on root without a slug
      const keysToRemove = [
        "hospital_primary_color",
        "hospital_secondary_color",
        "hospital_opaque_color",
        "hospital_name",
        "hospital_welcome_header",
        "hospital_welcome_writeup",
        "hospital_id",
        "hospital",
      ];
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      applyTheme(null);
      setIsLoading(false);
    }
  }, [slug, applyTheme]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex flex-col justify-center items-center px-4 py-8">
        <div className="flex flex-col items-center space-y-6">
          {/* Pulsing Medical Cross / Spinner */}
          <div className="relative flex items-center justify-center">
            <div className="absolute w-24 h-24 rounded-full border-4 border-emerald-500/30 animate-ping"></div>
            <div className="w-16 h-16 rounded-full border-4 border-t-emerald-600 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute text-emerald-600 font-bold text-xl">
              +
            </div>
          </div>
          <div className="text-center">
            <h2 className="text-emerald-800 text-xl font-bold tracking-wide animate-pulse">
              Loading Portal...
            </h2>
            {/* <p className="text-emerald-600 text-xs mt-1">
              Setting up secure branding environment
            </p> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex flex-col justify-center items-center px-4 py-8">
      {/* Main Welcome Card */}
      <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-300 hover:scale-105">

        {/* Header Section with Image */}
        <div className="bg-primary100 px-6 py-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 bg-white rounded-full p-4 shadow-lg">
              <img
                src={pana}
                alt={`${hospitalName} Healthcare`}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <h1 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
            {welcomeHeader}
          </h1>
          <div className="w-16 h-1 bg-white mx-auto rounded-full"></div>
        </div>

        {/* Content Section */}
        <div className="px-6 py-8">
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg leading-relaxed text-center mb-8">
            {welcomeWriteup}
          </p>

          {/* Action Buttons */}
          <div className="space-y-4">
            <Link to="/register" className="block">
              <button className="w-full py-4 bg-primary100 hover:bg-primary100/90 rounded-xl text-white text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                Get Started
              </button>
            </Link>

            <Link to="/login" className="block">
              <button className="w-full py-4 bg-white hover:bg-gray-50 rounded-xl text-primary100 text-lg font-semibold border-2 border-primary100 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg">
                Already have an account? Login
              </button>
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex justify-center items-center space-x-6 text-xs sm:text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-primary100 rounded-full"></div>
                <span>Secure</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-primary100 rounded-full"></div>
                <span>Trusted</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-primary100 rounded-full"></div>
                <span>Professional</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Text */}
      <p className="mt-6 text-center text-gray-500 text-sm max-w-md">
        Your health journey starts here. Join thousands of patients who trust {hospitalName} for their healthcare needs.
      </p>
    </div>
  );
}

export default Welcome;
